import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isUndefined } from 'lodash';
import moment from 'moment';
import { PublicCompanyAdjustmentStatus } from 'api/models/PublicCompanyAdjustmentStatus';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { Format } from 'common/types/format';
import { MessageBox } from 'components';
import { LedgerDialog } from 'components/Dialogs';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import BacksolveContext from 'context/BacksolveContext';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  AS_OF_DATE_ALIAS,
  compKeyMap,
  MARKET_ADJUSTMENT_LEDGER_TITLE,
  PERCENTILE_PARSE,
  PERCENTILE_SELECTION_A,
  PERCENTILE_SELECTION_B,
} from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/util/constants';
import MarketAdjustmentLedgerContext from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/util/MarketAdjustmentLedgerContext';
import { useRefreshHelpersWithContext } from 'pages/Valuations/components/RefreshGPCOption/hooks';
import { MEAN_LABEL, MEDIAN_LABEL, SPECIFIED_LABEL } from 'pages/Valuations/util/constants';
import { PublicComp } from 'pages/ValuationsAllocation/types';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useGetCompsByDate, usePrevious } from 'services/hooks';
import { ISO_DATE_FORMAT, swapMap } from 'utilities';
import parseDatabaseValue from 'utilities/parseDatabaseValue';
import { MarketAdjustmentLedgerProps, ZipCompData } from './MarketAdjustmentLedger.props';
import { ExtendedValuationApproachModel } from '../types';
import checkAsOfComps from './util/checkAsOfComps';
import colConfig from './util/colConfig';
import getRowConfig from './util/getRowConfig';
import parser from './util/parser';
import { isPercentileAlias, mapPercentileAliasToDatabaseValue } from './util/percentileHelpers';

const MarketAdjustmentLedger = ({ closeDialog }: MarketAdjustmentLedgerProps) => {
  const {
    gpcApproach,
    spreadsheets: gpcSheets,
    onMarketAdjustmentLedgerChange,
    approach,
  } = useContext(BacksolveContext);

  const { publicCompsAttrs, marketDate, marketCurrency } = useContext(ValuationContext);
  // Always use the market currency.
  const [format, formatDispatch] = useFormat({
    sourceCurrency: marketCurrency,
  }) as [Format, () => void];

  const { firmId } = useStore();
  const [asOfDate, setAsOfDate] = useState<string>(
    (gpcSheets?.marketAdjustmentSheet?.cells?.B2?.value as string) ?? ''
  );
  const previousAsOfDate = usePrevious(asOfDate);
  const [cellOptions, setCellOptions] = useState({});
  const { marketAdjustmentSheet } = gpcSheets;
  const { isDisabled } = marketAdjustmentSheet.tableData as any;
  const { getFlattenedComparisons } = useRefreshHelpersWithContext();
  const [fetchGpcCompData] = useGetCompsByDate();

  const [publicComps, setPublicComps] = useState<any>();

  const zipCompData = useCallback(
    ({ asOfComps, comps, fetchedAsOfComps }: ZipCompData) =>
      comps.map((comp: any) => {
        const { ticker_symbol: identifier, id: public_company, cap_iq_id } = comp;
        const asOfComp = asOfComps.find(
          (comp: any) => (!fetchedAsOfComps ? comp.ticker_symbol : comp.company_ticker?.value) === identifier
        );
        const mdComp = comp;
        const company_name = mdComp?.company_name?.value ?? comp.name;
        const savedComp = approach?.valuations_approach_backsolve?.public_companies_status?.find(
          ({ public_company: savedPublicCompId }) => savedPublicCompId === public_company
        );

        const { id, enabled } = savedComp || { id: undefined, enabled: true };

        return Object.entries(compKeyMap).reduce(
          (sofar: any, [key, value]) => {
            // eslint-disable-next-line no-param-reassign
            sofar.asOfComp[value] =
              (fetchedAsOfComps ? (asOfComp?.[key as keyof PublicComp] as PublicComp)?.value : asOfComp?.[key]) ?? 0;
            // eslint-disable-next-line no-param-reassign
            sofar.mdComp[value] = mdComp?.[key] ?? 0;
            return sofar;
          },
          {
            asOfComp: {},
            mdComp: {},
            company_name,
            public_company,
            identifier,
            id,
            enabled,
            cap_iq_id,
            isDisabled,
          }
        );
      }),
    [approach, isDisabled]
  );

  const publicCompsStatus = useMemo(
    () => approach?.valuations_approach_backsolve?.public_companies_status || [],
    [approach]
  );

  useEffect(() => {
    const handleDataProcessing = async () => {
      if (publicCompsStatus.length < (gpcApproach?.valuations_approach_gpc?.gpc_comparison?.length ?? 0)) {
        const currentCapIQs = publicCompsStatus.map(publicComp => publicComp.cap_iq_id);
        const newCompsCapIQ = (gpcApproach?.valuations_approach_gpc?.gpc_comparison ?? [])
          .filter(comparison => !currentCapIQs.includes(comparison.cap_iq_id))
          .map(comp => comp.cap_iq_id as string);

        const asOfDateMoment = moment(asOfDate).format(ISO_DATE_FORMAT);
        const asOfCompsData = await fetchGpcCompData(newCompsCapIQ, asOfDateMoment, format.currency.code, firmId, true);
        const flattenedComps = getFlattenedComparisons(asOfCompsData.results);

        flattenedComps.forEach(comp => {
          const {
            ltm_revenue,
            ltm_ebitda,
            ntm_revenue,
            ntm_ebitda,
            market_cap,
            enterprise_value,
            ticker_symbol,
            cap_iq_id,
          } = comp;

          const newPublicCompanyStatus = {
            ltm_revenue,
            ltm_ebitda,
            ntm_revenue,
            ntm_ebitda,
            market_cap,
            enterprise_value,
            ticker_symbol,
            cap_iq_id,
            valuation_approach_backsolve: approach?.valuations_approach_backsolve?.id ?? null,
            enabled: true,
          } as PublicCompanyAdjustmentStatus;

          publicCompsStatus.push(newPublicCompanyStatus);
        });
      }

      const zippedCompData = zipCompData({
        asOfComps: publicCompsStatus,
        comps: gpcApproach?.valuations_approach_gpc?.gpc_comparison ?? [],
        fetchedAsOfComps: false,
      });

      setPublicComps(zippedCompData);
    };
    const allAsOfValuesAreAvailable = checkAsOfComps(publicCompsStatus);
    if (gpcApproach && marketDate && asOfDate) {
      const identifiers = (gpcApproach?.valuations_approach_gpc?.gpc_comparison ?? []).map(
        ({ cap_iq_id }) => cap_iq_id as string
      );
      if (allAsOfValuesAreAvailable && (!previousAsOfDate || previousAsOfDate === asOfDate)) {
        handleDataProcessing();
      } else {
        const asOfDateMoment = moment(asOfDate).format(ISO_DATE_FORMAT);
        const asOfCompDataPromise = fetchGpcCompData(identifiers, asOfDateMoment, format.currency.code, firmId, true);
        asOfCompDataPromise.then(asOfComps => {
          if (asOfComps) {
            const zippedCompData = zipCompData({
              asOfComps: asOfComps.results,
              comps: gpcApproach?.valuations_approach_gpc?.gpc_comparison ?? [],
              fetchedAsOfComps: true,
            });
            setPublicComps(zippedCompData);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpcApproach, firmId, marketDate, asOfDate, publicCompsStatus, format]);

  const spreadsheets = useMemo(() => {
    if (publicComps) {
      const formattedMarketDate = moment(marketDate).format('MMM DD, YYYY');
      return [
        new SpreadsheetConfig({
          name: 'marketAdjustmentLedgerTable',
          parser,
          rowConfig: getRowConfig(publicComps, approach as ExtendedValuationApproachModel, isDisabled),
          tableData: {
            measurementDate: formattedMarketDate,
            asOfDate,
            publicComps,
            approach,
            isDisabled,
          },
          columns: colConfig,
          showTitlesColumn: false,
          showToolbar: true,
          currencyFormatter: false,
          unitsFormatter: false,
          totalParser: undefined,
          format: undefined,
        }),
      ];
    }
  }, [publicComps, marketDate, asOfDate, approach, isDisabled]);

  const { onChange, workbook } = useWorkbook(spreadsheets);

  const onLedgerChange = useCallback(
    (cell: EvaluatedCell, expr) => {
      if (cell.alias === AS_OF_DATE_ALIAS) {
        setAsOfDate(expr);
      } else {
        if ([PERCENTILE_SELECTION_A, PERCENTILE_SELECTION_B].includes(cell.alias ?? '')) {
          setCellOptions({
            ...cellOptions,
            [cell.alias]: expr,
          });
        }
        onChange(cell, expr);
      }
    },
    [cellOptions, onChange]
  );

  const onSave = useCallback(() => {
    if (!publicComps || !spreadsheets) {
      return;
    }
    const spreadsheet = spreadsheets[0];
    // only reversing the once cell that needs it that is passed on to greater scope
    // not neccessary to run the reverse parser on every field on this component, just the one cell
    const specifiedAdjustmentCell = spreadsheet.cells.SpecifiedAdjustment;
    const specifiedAdjustmentValue = parseDatabaseValue({
      value: specifiedAdjustmentCell.value,
      type: specifiedAdjustmentCell.dbType,
      format: specifiedAdjustmentCell.format,
      gridType: specifiedAdjustmentCell.gridType,
      decimalPlaces: (gpcSheets?.backsolveSheet?.fieldAttributes as any)?.specified_adjustment?.decimal_places,
    });
    let selectedAdjustment = spreadsheet.cells.SelectedAdjustment.value;
    if (![MEDIAN_LABEL, MEAN_LABEL, SPECIFIED_LABEL].includes(selectedAdjustment)) {
      // get the correct option to save by matching the value
      const percentileCells = Object.values(spreadsheet.cells).filter(
        ({ alias, parseAs }: any) => isPercentileAlias(alias) && parseAs === PERCENTILE_PARSE
      );
      const selectedPercentileCell = percentileCells.find(({ value }: any) =>
        selectedAdjustment.includes(value.toString())
      ) as any;
      selectedAdjustment = mapPercentileAliasToDatabaseValue(selectedPercentileCell.alias);
    }
    const data = {
      compData: [] as any[],
      AsOfDate: spreadsheet.cells.B2.value,
      Metric: spreadsheet.cells.B3.value,
      SelectedAdjustment: selectedAdjustment,
      AppliedAdjustment: spreadsheet.cells.AppliedAdjustment.value,
      SpecifiedAdjustment: specifiedAdjustmentValue,
    };
    const metricsMaps = new Map(Object.entries(compKeyMap));
    const invertedMetricsMaps = swapMap(metricsMaps);
    spreadsheet.rowConfig.forEach((row: any, index: number) => {
      if (index >= 3) {
        if (index < 3 + publicComps.length) {
          const publicCompStatus = {
            company_name: row.company_name,
            id: row.id,
            public_company: row.public_company,
            identifier: row.identifier,
            enabled: spreadsheet.cells[`D${index + 1}`].enabled,
            value: spreadsheet.cells[`D${index + 1}`].value,
            cap_iq_id: row.cap_iq_id,
          } as any;
          if (isUndefined(row.public_company)) {
            publicCompStatus.reference_for_backsolve = row.cap_iq_id;
          }
          Object.keys(row.asOfComp).forEach(key => {
            const alias = invertedMetricsMaps.get(key);
            const decimalPlaces =
              (publicCompsAttrs?.[alias as keyof typeof publicCompsAttrs]?.decimal_places as number) ?? 2;
            publicCompStatus[alias as keyof typeof publicCompStatus] = Number(row.asOfComp[key]).toFixed(decimalPlaces);
          });
          data.compData.push(publicCompStatus);
        }
        if (index >= 3 + publicComps.length + 2 && index < 3 + publicComps.length + 4) {
          data[`${row.alias}_selection` as keyof typeof data] = spreadsheet.cells[`C${index + 1}`].value;
        }
        data[row.alias as keyof typeof data] = spreadsheet.cells[`D${index + 1}`].value;
      }
    });
    onMarketAdjustmentLedgerChange(data);
    closeDialog();
  }, [closeDialog, onMarketAdjustmentLedgerChange, publicComps, spreadsheets, gpcSheets, publicCompsAttrs]);

  const memoizedCellOptions = useMemo(() => ({ cellOptions }), [cellOptions]);

  if (!asOfDate || !gpcApproach) {
    return (
      <LedgerDialog id="market-adjustment-ledger" onClose={closeDialog} title={MARKET_ADJUSTMENT_LEDGER_TITLE}>
        <MessageBox
          title="No 'As of Date' or 'Guideline Public Company'"
          tagline="Please select an As of Date and a Guideline Public Company in the Market Adjustment Table"
        />
      </LedgerDialog>
    );
  }

  if (spreadsheets?.length && publicComps) {
    return (
      <LedgerDialog disabled={isDisabled} id="market-adjustment-ledger" onClose={closeDialog} onSave={onSave}>
        <MarketAdjustmentLedgerContext.Provider value={memoizedCellOptions}>
          <ScalarSpreadsheet
            {...spreadsheets[0]}
            tableTerms={{ tableSlug: 'market-adjustment-ledger' }}
            workbook={workbook}
            onChange={onLedgerChange}
            sheet={spreadsheets[0]}
            displayLegend={false}
            alwaysDisplayLegend={false}
            isLedgerTable
            format={format}
            formatDispatch={formatDispatch}
          />
        </MarketAdjustmentLedgerContext.Provider>
      </LedgerDialog>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}>
      <div className="spinner" />
      <p>Loading {MARKET_ADJUSTMENT_LEDGER_TITLE}...</p>
    </div>
  );
};

export default MarketAdjustmentLedger;
